import React, { useState, useEffect, useRef } from 'react'
import { HiCheckCircle, HiXCircle, HiClock, HiPlay, HiShieldCheck, HiOfficeBuilding, HiCollection, HiUsers, HiExclamationCircle, HiCog, HiMail, HiGlobe, HiSearch } from 'react-icons/hi'
import { Spinner } from 'flowbite-react'
import { toast } from 'react-hot-toast'
import OtpAccessPanel from './OtpAccessPanel'
import { fetchLeadStatus, isReportReadyStatus } from '../constants/inviteCode'
import { fireConfetti, stopConfetti, resetConfettiGuard } from '../utils/confetti'

// Icon mapping for dynamic icons from API
const iconMap = {
  'play-circle': HiPlay,
  'shield-check': HiShieldCheck,
  'building-office': HiOfficeBuilding,
  'office-building': HiOfficeBuilding,
  'sitemap': HiCollection,
  'collection': HiCollection,
  'users': HiUsers,
  'check-circle': HiCheckCircle,
  'exclamation-circle': HiExclamationCircle,
  'x-circle': HiXCircle,
  'clock': HiClock,
  'cog': HiCog,
  'mail': HiMail,
  'globe': HiGlobe,
  'search': HiSearch
}

const VENDOR_LOOKUP_STEP_ID = 'vendor_lookup'

const isVendorStep = (step) => step?.id === VENDOR_LOOKUP_STEP_ID

const resolveVendorIdentificationStatus = (data, step) => {
  if (step?.identificationStatus) return step.identificationStatus

  const fromStepResults = data?.stepResults?.[VENDOR_LOOKUP_STEP_ID]?.identificationStatus
  if (fromStepResults) return fromStepResults

  const fromSteps = data?.steps?.find((item) => item.id === VENDOR_LOOKUP_STEP_ID)?.identificationStatus
  if (fromSteps) return fromSteps

  return null
}

const getVendorCompletionMessage = (data, step) => {
  const identificationStatus = resolveVendorIdentificationStatus(data, step)
  if (identificationStatus === 'identified') return '3rd Party vendor identified'
  if (identificationStatus === 'not_identified') return 'No 3rd Party vendor identified'
  return null
}

const mergeSteps = (existingSteps, incomingSteps) => {
  if (!incomingSteps?.length) return existingSteps
  if (!existingSteps?.length) return incomingSteps

  const incomingById = new Map(incomingSteps.map((step) => [step.id, step]))
  const merged = existingSteps.map((existing) => {
    const incoming = incomingById.get(existing.id)
    return incoming ? { ...existing, ...incoming } : existing
  })

  for (const incoming of incomingSteps) {
    if (!existingSteps.some((step) => step.id === incoming.id)) {
      merged.push(incoming)
    }
  }

  return merged
}

const DEFAULT_STATUS_COLORS = {
  completed: {
    background: 'bg-green-50 dark:bg-green-900/20',
    border: 'border-green-200 dark:border-green-800',
    text: 'text-green-700 dark:text-green-300',
    icon: 'text-green-500',
  },
  active: {
    background: 'bg-blue-50 dark:bg-blue-900/20',
    border: 'border-blue-200 dark:border-blue-800',
    text: 'text-blue-700 dark:text-blue-300',
    icon: 'text-blue-500',
  },
  error: {
    background: 'bg-red-50 dark:bg-red-900/20',
    border: 'border-red-200 dark:border-red-800',
    text: 'text-red-700 dark:text-red-300',
    icon: 'text-red-500',
  },
  pending: {
    background: 'bg-gray-100 dark:bg-gray-800',
    border: 'border-gray-200 dark:border-gray-700',
    text: 'text-gray-700 dark:text-gray-300',
    icon: 'text-gray-400 dark:text-gray-500',
  },
}

const DOMAIN_VALIDATION_FAILURE_MESSAGE =
  'Domain failed validation. Was it a typo?'

const firstNonEmptyString = (...values) => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim() && value.trim().toLowerCase() !== 'error') {
      return value.trim()
    }
  }
  return null
}

const getFailedStepId = (data) => data?.failedStep || data?.failed_step || null

const isJobFailedData = (data) => {
  const status = String(data?.status ?? '').toLowerCase()
  if (status === 'error' || status === 'failed') return true
  if (getFailedStepId(data)) return true
  if (Array.isArray(data?.steps) && data.steps.some((step) => step?.status === 'error')) {
    return true
  }
  return false
}

const getValidationFailureMessage = (data, step = null) => {
  const fromStep = firstNonEmptyString(
    step?.errorMessage,
    step?.error_message,
    step?.userMessage,
    step?.user_message,
    step?.message,
  )
  if (fromStep && fromStep.toLowerCase() !== 'failed') return fromStep

  const failedStepId = step?.id || getFailedStepId(data)
  if (failedStepId === 'domain_verified' || failedStepId === 'ownership_verification') {
    return DOMAIN_VALIDATION_FAILURE_MESSAGE
  }

  const fromJob = firstNonEmptyString(
    data?.errorMessage,
    data?.error_message,
    data?.failureContext,
    data?.failure_context,
    data?.error,
    data?.message,
  )
  if (fromJob && fromJob.toLowerCase() !== 'failed') return fromJob

  return DOMAIN_VALIDATION_FAILURE_MESSAGE
}

const getStepStatusMessage = (step, data) => {
  if (isVendorStep(step)) {
    if (step.status === 'active') return 'Processing...'
    if (step.status === 'error') return getValidationFailureMessage(data, step)
    if (step.status === 'completed') {
      return getVendorCompletionMessage(data, step) ?? 'Completed'
    }
  }

  if (step.status === 'active') return 'Processing...'
  if (step.status === 'error') return getValidationFailureMessage(data, step)
  if (step.status === 'completed') return 'Completed'
  return step.description || 'Processing...'
}

const isJobCompleteData = (data) =>
  isReportReadyStatus(data?.status) || Number(data?.progress) >= 100

const syncStepStatusesFromApi = (incomingSteps, data, prevStatuses = {}) => {
  if (!incomingSteps?.length) return prevStatuses

  const jobComplete = isJobCompleteData(data)
  const currentStepId = data?.currentStep
  const stepIndexById = new Map(incomingSteps.map((step, index) => [step.id, index]))
  const currentIndex = currentStepId != null ? (stepIndexById.get(currentStepId) ?? -1) : -1
  const next = { ...prevStatuses }

  incomingSteps.forEach((step, index) => {
    let status = step.status || 'pending'

    if (jobComplete) {
      status = 'completed'
    } else if (status === 'pending') {
      if (currentIndex >= 0 && index < currentIndex) {
        status = 'completed'
      } else if (step.id === currentStepId || index === currentIndex) {
        status = 'active'
      } else {
        const laterStarted = incomingSteps
          .slice(index + 1)
          .some((later) => later.status === 'completed' || later.status === 'active')
        if (laterStarted) status = 'completed'
      }
    }

    if (prevStatuses[step.id]?.status === 'completed' && status !== 'error') {
      status = 'completed'
    }

    if (status !== 'pending') {
      const normalizedStep = { ...step, status }
      next[step.id] = {
        status,
        message: getStepStatusMessage(normalizedStep, data),
      }
    }
  })

  return next
}

const DomainValidationProgress = ({
  jobId,
  leadId,
  domain, 
  email, 
  onComplete, 
  onError,
  onOfflineMode,
  onInviteCodeVerified,
}) => {
  const [steps, setSteps] = useState([])
  const [, setCurrentStep] = useState('')
  const [progress, setProgress] = useState(0)
  const [isConnected, setIsConnected] = useState(true)
  const [isUsingFallback, setIsUsingFallback] = useState(false)
  const [connectionRetries, setConnectionRetries] = useState(0)
  const [validationResult, setValidationResult] = useState(null)
  const [totalEstimatedTime, setTotalEstimatedTime] = useState(0)
  const [elapsedTime, setElapsedTime] = useState(0)
  const [statusColors, setStatusColors] = useState({})
  const [stepStatuses, setStepStatuses] = useState({})
  const [isAnimating, setIsAnimating] = useState(false)
  const [isCompleted, setIsCompleted] = useState(false)
  const [isFailed, setIsFailed] = useState(false)
  const [failureMessage, setFailureMessage] = useState('')
  const [finalElapsedTime, setFinalElapsedTime] = useState(null)
  
  const eventSourceRef = useRef(null)
  const pollIntervalRef = useRef(null)
  const startTimeRef = useRef(Date.now())
  const isCompletedRef = useRef(false)
  const isFailedRef = useRef(false)
  const isConnectingRef = useRef(false)
  const lastMessageTimeRef = useRef(Date.now())
  const sseTimeoutRef = useRef(null)
  const timeIntervalRef = useRef(null)
  const stepsRef = useRef(steps)
  const stepStatusesRef = useRef(stepStatuses)
  const progressRef = useRef(progress)
  const isAnimatingRef = useRef(isAnimating)
  const validationResultRef = useRef(validationResult)
  const handleValidationUpdateRef = useRef(() => {})

  stepsRef.current = steps
  stepStatusesRef.current = stepStatuses
  progressRef.current = progress
  isAnimatingRef.current = isAnimating
  validationResultRef.current = validationResult

  const stopLiveUpdates = () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close()
      eventSourceRef.current = null
    }
    isConnectingRef.current = false
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current)
      pollIntervalRef.current = null
    }
    if (sseTimeoutRef.current) {
      clearTimeout(sseTimeoutRef.current)
      sseTimeoutRef.current = null
    }
  }

  const stopElapsedTimer = () => {
    if (timeIntervalRef.current) {
      clearInterval(timeIntervalRef.current)
      timeIntervalRef.current = null
    }
    const seconds = Math.floor((Date.now() - startTimeRef.current) / 1000)
    setElapsedTime(seconds)
    setFinalElapsedTime(seconds)
  }

  const markValidationComplete = () => {
    if (isCompletedRef.current || isFailedRef.current) return
    isCompletedRef.current = true
    setIsCompleted(true)
    stopElapsedTimer()
    stopLiveUpdates()
    fireConfetti()
  }

  const markValidationFailed = (message) => {
    if (isFailedRef.current || isCompletedRef.current) return false
    isFailedRef.current = true
    setIsFailed(true)
    setFailureMessage(message || DOMAIN_VALIDATION_FAILURE_MESSAGE)
    stopElapsedTimer()
    stopLiveUpdates()
    return true
  }

  const applyVendorStepCompletion = (data) => {
    const vendorStep =
      stepsRef.current.find(isVendorStep) ??
      data?.steps?.find(isVendorStep)
    if (!vendorStep) return

    const message = getVendorCompletionMessage(data, vendorStep)
    if (!message) return

    setStepStatuses((prev) => ({
      ...prev,
      [vendorStep.id]: {
        status: 'completed',
        message,
      },
    }))
  }

  // Initialize SSE connection
  useEffect(() => {
    if (!jobId) return

    // Reset state for new validation
    setIsCompleted(false)
    setIsFailed(false)
    setFailureMessage('')
    isCompletedRef.current = false
    isFailedRef.current = false
    setFinalElapsedTime(null)
    resetConfettiGuard()
    stopConfetti()
    startTimeRef.current = Date.now()
    setElapsedTime(0)

    const connectSSE = () => {
      // Prevent multiple connections
      if (isConnectingRef.current || eventSourceRef.current) {
        console.log('SSE connection already in progress or exists, skipping')
        return
      }
      
      isConnectingRef.current = true
      try {
        const eventSource = new EventSource(`https://api.cityseecityfix.com/api/v1/leads/${jobId}/stream`)
        eventSourceRef.current = eventSource

        eventSource.onopen = () => {
          console.log('SSE connection opened for job:', jobId.substring(0, 8) + '...')
          console.log('SSE readyState:', eventSource.readyState)
          setIsConnected(true)
          setConnectionRetries(0)
        }

        eventSource.onmessage = (event) => {
          console.log('SSE message received for job:', jobId.substring(0, 8) + '...')
          console.log('SSE message data:', event.data)
          console.log('SSE readyState:', eventSource.readyState)
          
          // Update last message time
          lastMessageTimeRef.current = Date.now()
          
          // Clear any existing timeout
          if (sseTimeoutRef.current) {
            clearTimeout(sseTimeoutRef.current)
          }
          
          // Set a timeout to start polling if we don't receive messages for 10 seconds
          sseTimeoutRef.current = setTimeout(() => {
            console.log('No SSE messages for 10 seconds, starting polling fallback')
            setIsUsingFallback(true)
            startPollingFallback()
          }, 10000)
          
          try {
            const data = JSON.parse(event.data)
            handleValidationUpdateRef.current(data)
          } catch (error) {
            console.error('Error parsing SSE data:', error)
          }
        }

        eventSource.onerror = (error) => {
          console.error('SSE connection error for job:', jobId.substring(0, 8) + '...', error)
          console.log('SSE readyState:', error.target?.readyState)
          
          // Reset connection flag
          isConnectingRef.current = false
          
          // Clean up the event source
          if (eventSourceRef.current) {
            eventSourceRef.current.close()
            eventSourceRef.current = null
          }
          
          // Check if validation is already complete/failed - don't show connection lost message
          if (
            isCompletedRef.current ||
            isFailedRef.current ||
            validationResultRef.current ||
            progressRef.current === 100
          ) {
            console.log('Validation already finished, not starting polling fallback')
            // Don't set setIsConnected(false) when validation is terminal - this prevents the "Connection lost" warning
            return
          }
          
          // Only show connection lost message if validation isn't complete
          setIsConnected(false)
          
          // Check if this is a 404 (endpoint doesn't exist yet)
          if (error.target?.readyState === EventSource.CLOSED) {
            console.log('SSE connection closed for job:', jobId.substring(0, 8) + '...')
            // Only fallback to polling if validation isn't complete
            startPollingFallback()
            return
          }
          
          // For other errors, try polling fallback
          console.log('SSE error, falling back to polling for job:', jobId.substring(0, 8) + '...')
          setIsUsingFallback(true)
          startPollingFallback()
        }

      } catch (error) {
        console.error('Error creating SSE connection:', error)
        isConnectingRef.current = false
        setIsUsingFallback(true)
        startPollingFallback()
      }
    }

    const hydrateFromStatus = async () => {
      try {
        const data = await fetchLeadStatus(jobId)
        handleValidationUpdateRef.current(data)
      } catch (error) {
        console.error('Failed to hydrate validation status:', error)
      }
    }

    hydrateFromStatus()
    connectSSE()

    // Update elapsed time every second until completion or failure
    timeIntervalRef.current = setInterval(() => {
      if (!isCompletedRef.current && !isFailedRef.current) {
        setElapsedTime(Math.floor((Date.now() - startTimeRef.current) / 1000))
      }
    }, 1000)

    // Cleanup on unmount
    return () => {
      isConnectingRef.current = false
      stopConfetti()
      if (eventSourceRef.current) {
        eventSourceRef.current.close()
        eventSourceRef.current = null
      }
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
        pollIntervalRef.current = null
      }
      if (sseTimeoutRef.current) {
        clearTimeout(sseTimeoutRef.current)
        sseTimeoutRef.current = null
      }
      if (timeIntervalRef.current) {
        clearInterval(timeIntervalRef.current)
        timeIntervalRef.current = null
      }
    }
    // startPollingFallback is declared below this effect, so naming it here
    // would evaluate it in the temporal dead zone and throw. It is also
    // recreated every render, which would tear down and re-open the SSE
    // connection on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, connectionRetries])

  // Polling fallback
  const startPollingFallback = () => {
    console.log('Starting polling fallback for job:', jobId.substring(0, 8) + '...')
    setIsUsingFallback(true)
    pollIntervalRef.current = setInterval(async () => {
      try {
        const response = await fetch(`https://api.cityseecityfix.com/api/v1/leads/${jobId}/status`)
        if (response.ok) {
          const data = await response.json()
          handleValidationUpdateRef.current(data)
        } else if (response.status === 404) {
          console.log('Validation endpoint not available yet for job:', jobId.substring(0, 8) + '...')
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current)
            pollIntervalRef.current = null
          }
          onOfflineMode?.()
        }
      } catch (error) {
        console.error('Polling error for job:', jobId.substring(0, 8) + '...', error)
      }
    }, 5000)
  }

  // Handle validation updates from API
  const handleValidationUpdate = (data) => {
    console.log('Validation update:', data)

    // Once successfully completed, ignore late failure / in-progress payloads so
    // we never paint error steps over a finished "Validated" UI.
    if (isCompletedRef.current && !isJobCompleteData(data)) {
      return
    }

    if (isFailedRef.current && !isJobFailedData(data) && !isJobCompleteData(data)) {
      return
    }
    
    // Always update step definitions if provided (even on completion)
    if (data.steps) {
      const mergedSteps = mergeSteps(stepsRef.current, data.steps)
      setSteps(mergedSteps)
      setStatusColors(data.statusColors || {})
      setTotalEstimatedTime(data.totalEstimatedTime || 0)
      setStepStatuses((prev) => syncStepStatusesFromApi(mergedSteps, data, prev))
      
      // Update current step and progress from the API data
      if (data.currentStep) {
        setCurrentStep(data.currentStep)
      }
      if (data.progress !== undefined) {
        const incomingProgress = Number(data.progress)
        if (isJobCompleteData(data) || incomingProgress >= progressRef.current) {
          setProgress(incomingProgress)
        }
      }
    }

    if (isJobFailedData(data)) {
      if (isCompletedRef.current) {
        return
      }

      const failedStepId = getFailedStepId(data)
      const failedStep =
        (data.steps || stepsRef.current).find((step) => step.id === failedStepId) ||
        (data.steps || stepsRef.current).find((step) => step.status === 'error')
      const message = getValidationFailureMessage(data, failedStep)

      if (!markValidationFailed(message)) {
        return
      }

      if (failedStep) {
        updateStepStatus(failedStep.id, 'error', message)
      }

      toast.error(
        <div className="flex items-center space-x-2">
          <HiExclamationCircle className="w-5 h-5 text-red-500" />
          <div>
            <div className="font-medium">Validation Error</div>
            <div className="text-sm">{message}</div>
          </div>
        </div>,
        { duration: 8000 }
      )
      onError?.(message)
      return
    }
    
    // Check if validation is complete and stop polling
    if (isJobCompleteData(data)) {
      markValidationComplete()
      applyVendorStepCompletion(data)
      
      // Stop polling if it's running
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
        pollIntervalRef.current = null
      }
      
      // Clear the SSE timeout since we're done
      if (sseTimeoutRef.current) {
        clearTimeout(sseTimeoutRef.current)
        sseTimeoutRef.current = null
      }

      if (data.steps?.length) {
        setStepStatuses((prev) => syncStepStatusesFromApi(mergeSteps(stepsRef.current, data.steps), data, prev))
      }

      const currentSteps = data.steps ?? stepsRef.current
      const currentProgress = data.progress !== undefined ? data.progress : progressRef.current
      const stepsAlreadyComplete =
        currentSteps.length > 0 &&
        currentSteps.every((step) => step.status === 'completed')

      // If we get a complete status immediately, animate through the steps
      if (
        currentSteps.length > 0 &&
        currentProgress === 0 &&
        !isAnimatingRef.current &&
        !stepsAlreadyComplete &&
        !isJobCompleteData(data)
      ) {
        setIsAnimating(true)
        animateStepsToCompletion(data, currentSteps)
      } else {
        setValidationResult(data)
        setProgress(100)
        onComplete?.(data)
      }
      return // Exit early to prevent further processing
    }
    
    // Update progress and current step (only if not already set from steps)
    if (data.progress !== undefined && !data.steps) {
      setProgress(data.progress)
    }
    if (data.currentStep && !data.steps) {
      setCurrentStep(data.currentStep)
    }
    
    // Handle different update types
    switch (data.type) {
      case 'step_update':
        updateStepStatus(data.stepId, data.status, data.message)
        if (data.progress !== undefined) {
          setProgress(data.progress)
        }
        break
        
      case 'step_complete': {
        const completedStep =
          stepsRef.current.find((step) => step.id === data.stepId) ??
          data?.steps?.find((step) => step.id === data.stepId)
        const message = completedStep && isVendorStep(completedStep)
          ? (getVendorCompletionMessage(data, completedStep) ?? data.message)
          : data.message
        updateStepStatus(data.stepId, 'completed', message)
        break
      }
        
      case 'step_error': {
        if (isCompletedRef.current) {
          break
        }
        const failedStep =
          stepsRef.current.find((step) => step.id === data.stepId) ??
          data?.steps?.find((step) => step.id === data.stepId)
        const message = getValidationFailureMessage(
          data,
          failedStep ? { ...failedStep, message: data.message } : { id: data.stepId, message: data.message },
        )
        if (!markValidationFailed(message)) {
          break
        }
        updateStepStatus(data.stepId, 'error', message)
        toast.error(
          <div className="flex items-center space-x-2">
            <HiExclamationCircle className="w-5 h-5 text-red-500" />
            <div>
              <div className="font-medium">Validation Error</div>
              <div className="text-sm">{message}</div>
            </div>
          </div>,
          { duration: 8000 }
        )
        onError?.(message)
        break
      }
        
      case 'validation_complete': {
        const completedStep =
          stepsRef.current.find((step) => step.id === data.stepId) ??
          data?.steps?.find((step) => step.id === data.stepId)
        const message = completedStep && isVendorStep(completedStep)
          ? (getVendorCompletionMessage(data, completedStep) ?? data.message)
          : data.message
        updateStepStatus(data.stepId, 'completed', message)
        markValidationComplete()
        setProgress(100)
        setValidationResult(data)
        applyVendorStepCompletion(data)
        onComplete?.(data)
        break
      }
        
      default:
        // Handle heartbeat messages
        if (data.type === 'heartbeat') {
          console.log('Heartbeat received, poll_count:', data.poll_count)
        }
        break
    }
  }

  handleValidationUpdateRef.current = handleValidationUpdate

  // Update individual step status
  const updateStepStatus = (stepId, status, message) => {
    setStepStatuses(prev => ({
      ...prev,
      [stepId]: { status, message }
    }))
  }

  // Animate through steps when API sends immediate completion
  const animateStepsToCompletion = (finalData, stepsToAnimate) => {
    const stepDelay = 800 // 800ms between each step
    const progressIncrement = 100 / stepsToAnimate.length

    stepsToAnimate.forEach((step, index) => {
      setTimeout(() => {
        // Update step status to active
        updateStepStatus(step.id, 'active', 'Processing...')
        setCurrentStep(step.id)
        setProgress((index + 1) * progressIncrement)
        
        // After a short delay, mark as completed
        setTimeout(() => {
          const message = isVendorStep(step)
            ? (getVendorCompletionMessage(finalData, step) ?? 'Completed successfully')
            : 'Completed successfully'
          updateStepStatus(step.id, 'completed', message)
          
          // If this is the last step, show final results
          // Timer/confetti already handled when API reported completion
          if (index === stepsToAnimate.length - 1) {
            setTimeout(() => {
              setProgress(100)
              setValidationResult(finalData)
              markValidationComplete()
              onComplete?.(finalData)
            }, 500)
          }
        }, 600)
      }, index * stepDelay)
    })
  }

  // Get step icon component
  const getStepIcon = (step) => {
    const IconComponent = iconMap[step.icon] || HiCog // Default fallback
    
    const stepStatus = stepStatuses[step.id]?.status || step.status || 'pending'
    
    switch (stepStatus) {
      case 'completed':
        return <HiCheckCircle className="h-6 w-6" />
      case 'error':
        return <HiXCircle className="h-6 w-6" />
      case 'active':
        return <Spinner size="sm" />
      default:
        return <IconComponent className="h-6 w-6" />
    }
  }

  // Get step styling based on status and API-provided colors
  const getStepStyles = (step) => {
    const stepStatus = stepStatuses[step.id]?.status || step.status || 'pending'
    const defaults = DEFAULT_STATUS_COLORS[stepStatus] || DEFAULT_STATUS_COLORS.pending
    const colorConfig = { ...defaults, ...statusColors[stepStatus] }
    
    return {
      container: `${colorConfig.background} ${colorConfig.border} border rounded-lg p-4 transition-all duration-300`,
      text: colorConfig.text,
      icon: colorConfig.icon,
    }
  }

  // Format time display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`
  }

  // Calculate estimated time remaining
  const getEstimatedTimeRemaining = () => {
    if (!totalEstimatedTime) return 'Calculating...'
    const remaining = Math.max(0, totalEstimatedTime - elapsedTime)
    return formatTime(remaining)
  }

  const displayedElapsedTime = finalElapsedTime ?? elapsedTime
  const isTerminal = isCompleted || isFailed

  return (
    <div className="w-full max-w-4xl mx-auto">
      {/* Header with Firefly Background */}
      <div className="relative text-center mb-8 p-6 rounded-lg overflow-hidden">
        {/* Firefly CSS Background Animation — static when complete/failed */}
        <div className={`absolute inset-0 bg-gradient-to-r from-blue-400/10 via-purple-400/10 to-blue-400/10 ${isTerminal ? '' : 'animate-pulse'}`} />
        {!isTerminal && (
          <>
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(59,130,246,0.1),transparent_50%)] animate-spin-slow" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(147,51,234,0.1),transparent_50%)] animate-spin-slow" style={{ animationDirection: 'reverse', animationDuration: '8s' }} />
          </>
        )}
        
        <div className="relative z-10">
          <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            {isFailed
              ? `Validation failed for ${domain}`
              : isCompleted
                ? `Validated ${domain}`
                : `Validating ${domain}`}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {isFailed
              ? (failureMessage || DOMAIN_VALIDATION_FAILURE_MESSAGE)
              : isCompleted
                ? 'A link to the full report has been emailed'
                : "We're verifying your municipal domain and discovering your city's structure"}
          </p>

          {isCompleted && onInviteCodeVerified && (
            <div className="max-w-md mx-auto mt-6 p-5 bg-white/80 dark:bg-gray-800/80 backdrop-blur rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
              <OtpAccessPanel
                email={email}
                domain={domain}
                compact
                onVerified={({ otp }) => onInviteCodeVerified({
                  ...validationResult,
                  domain,
                  jobId,
                  leadId,
                  email,
                  otp,
                })}
              />
            </div>
          )}
          
          {/* Connection Status */}
          <div className="flex items-center justify-center space-x-6 text-sm">
            {!isTerminal && (
              <>
                <div className="flex items-center space-x-2">
                  <div className={`w-3 h-3 rounded-full ${
                    isUsingFallback 
                      ? 'bg-orange-500 animate-pulse' 
                      : isConnected 
                        ? 'bg-green-500 animate-pulse' 
                        : 'bg-red-500'
                  }`}></div>
                  <span className="text-gray-600 dark:text-gray-400">
                    {isUsingFallback 
                      ? 'Polling Mode' 
                      : isConnected 
                        ? 'Connected' 
                        : 'Offline Mode'
                    }
                  </span>
                </div>
                <div className="text-gray-600 dark:text-gray-400">
                  Est. remaining: {getEstimatedTimeRemaining()}
                </div>
              </>
            )}
            <div className="text-gray-600 dark:text-gray-400">
              {isTerminal ? 'Total time' : 'Elapsed'}: {formatTime(displayedElapsedTime)}
            </div>
          </div>
        </div>
      </div>

      {/* Animated Progress Bar */}
      <div className="mb-8">
        <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
          <span>Overall Progress</span>
          <span>{progress}%</span>
        </div>
        <div className="relative w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4 overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-blue-500 via-purple-500 to-green-500 rounded-full transition-all duration-500 ease-out relative"
            style={{ width: `${progress}%` }}
          >
            {!isTerminal && (
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer" />
            )}
          </div>
        </div>
      </div>

      {/* Dynamic Steps Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {steps.length === 0 ? (
          <div className="col-span-full text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading validation steps...</p>
          </div>
        ) : (
          steps.map((step, index) => {
          const styles = getStepStyles(step)
          const stepStatus = stepStatuses[step.id]?.status || step.status || 'pending'
          
          return (
            <div 
              key={step.id}
              className={styles.container}
              role="status"
              aria-label={`Step ${index + 1}: ${step.name}`}
            >
              <div className="flex items-start space-x-4">
                {/* Step Icon */}
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${styles.icon}`}>
                  {getStepIcon(step)}
                </div>
                
                {/* Step Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className={`font-semibold ${styles.text} truncate`}>
                      {step.name}
                    </h4>
                    <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap ml-2">
                      {step.estimatedTime}s
                    </span>
                  </div>
                  
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    {step.description}
                  </p>
                  
                  {/* Step-specific progress bar */}
                  {stepStatus === 'active' && (
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1">
                      <div className="bg-blue-500 h-1 rounded-full animate-pulse" style={{ width: '60%' }} />
                    </div>
                  )}
                  
                  {/* Status message */}
                  {stepStatuses[step.id]?.message && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {stepStatuses[step.id].message}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )
        })
        )}
      </div>

      {!isConnected && !isUsingFallback && !isTerminal && (
        <div className="mt-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
          <div className="flex items-center space-x-2">
            <HiClock className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
            <span className="text-sm text-yellow-800 dark:text-yellow-200">
              Connection lost. We'll continue processing in the background and email you when complete.
            </span>
          </div>
        </div>
      )}

      {/* Results Display */}
      {validationResult && (
        <div className="mt-8 p-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <div className="flex items-center space-x-2 mb-4">
            <HiCheckCircle className="h-6 w-6 text-green-500" />
            <h4 className="text-lg font-semibold text-green-800 dark:text-green-200">
              Validation Complete!
            </h4>
          </div>
          
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium text-green-700 dark:text-green-300">Departments:</span>
              <span className="ml-2 text-green-600 dark:text-green-400">
                {validationResult.departmentsCount || 0}
              </span>
            </div>
            <div>
              <span className="font-medium text-green-700 dark:text-green-300">Contacts:</span>
              <span className="ml-2 text-green-600 dark:text-green-400">
                {validationResult.contactsCount || 0}
              </span>
            </div>
          </div>
          
          <p className="text-sm text-green-600 dark:text-green-400 mt-2">
            Total time: {formatTime(displayedElapsedTime)}
          </p>
        </div>
      )}
    </div>
  )
}

export default DomainValidationProgress